import React, { useRef, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { encryptCheckout } from '../../utils/encryptionUtility';
import { useValidation } from '../../hooks/useValidation';
import {
	postNewMembershipSignup,
	setCardDisplayInfo
} from '../../containers/customerDetails/customerDetailsSlice';
import { getBrowserSignature } from '../../utils/getBrowserSignature';

import OrderSummary from './components/OrderSummary';
import AgreeToChargeCard from './components/AgreeToChargeCard';
import BillingInfo from './components/BillingInfo';
import MoneyBackGuarantee from './components/MoneyBackGuarantee';
import PaymentInfo from './components/PaymentInfo';
import Guarantees from './components/Guarantees';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import LoadingButton from '@mui/lab/LoadingButton';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';

const SUPPORTED_BRANDS = ['Visa', 'Mastercard', 'American Express', 'Discover'];

const INITIAL_VALUES = {
	firstName: '',
	lastName: '',
	address1: '',
	address2: '',
	city: '',
	state: '',
	zipCode: '',
	email: '',
	phone: '',
	privacyChecked: false,
	cardNumber: '',
	expiryDate: '',
	cvc: '',
	billingZip: '',
	typeOfCard: '',
	termsChecked: false,
	offerDetailsChecked: false,
	marketingChecked: false
};

const REQUIRED_FIELDS = [
	'firstName',
	'lastName',
	'address1',
	'city',
	'state',
	'zipCode',
	'email',
	'phone',
	'cardNumber',
	'expiryDate',
	'cvc',
	'billingZip',
	'termsChecked',
	'privacyChecked',
	'offerDetailsChecked',
	'marketingChecked'
];

const CheckoutForm = ({ membershipListData }) => {
	const { t } = useTranslation('common');
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const signatureObj = getBrowserSignature();
	const paymentInfoRef = useRef();

	const {
		isLoading: submitDataIsLoading,
		hasCompleted: submitCompleted,
		result: submitResult,
		error: submitError
	} = useSelector(
		(state) => state.customerDetailsSlice.postNewMembershipSignup
	);

	const { result } = useSelector(
		(state) => state.customerDetailsSlice.postBrowserDetails
	);

	const validationRules = useMemo(
		() => ({
			firstName: t('billingInfo:firstName'),
			lastName: t('billingInfo:lastName'),
			address1: t('billingInfo:address'),
			city: t('billingInfo:city'),
			state: t('billingInfo:state'),
			zipCode: t('billingInfo:zip'),
			email: t('billingInfo:email'),
			phone: t('billingInfo:phone'),
			cardNumber: t('paymentInfo:cardNumber'),
			expiryDate: t('paymentInfo:expiryDate'),
			cvc: t('paymentInfo:cvv'),
			billingZip: t('paymentInfo:billingZip'),
			termsChecked: t('agreeToChargeCard:termsChecked'),
			privacyChecked: t('agreeToChargeCard:privacyChecked'),
			offerDetailsChecked: t('agreeToChargeCard:offerDetailsChecked'),
			marketingChecked: t('agreeToChargeCard:marketingChecked')
		}),
		[t]
	);

	const {
		formData,
		errors,
		handleInputChange,
		handleInputBlur,
		validateForm,
		reset
	} = useValidation(INITIAL_VALUES, validationRules, t);

	const cardBrandError = useMemo(() => {
		const cardNumber = formData.cardNumber?.replace(/\D/g, '') || '';

		if (cardNumber.length < 12) return null;

		const brand = formData.typeOfCard;

		if (!brand) {
			return t('unrecognizedCard');
		}

		if (!SUPPORTED_BRANDS.includes(brand)) {
			return t('unsupportedCardBrand', { brand });
		}

		return null;
	}, [formData.cardNumber, formData.typeOfCard, t]);

	const isFormComplete = useMemo(() => {
		const allFieldsFilled = REQUIRED_FIELDS.every((field) => {
			const value = formData[field];
			if (typeof value === 'boolean') {
				return value === true;
			}
			return value && value.trim() !== '';
		});

		const noErrors = Object.values(errors).every((error) => !error);

		return allFieldsFilled && noErrors && !cardBrandError;
	}, [formData, errors, cardBrandError]);

	useEffect(() => {
		if (submitCompleted && submitResult && !submitError) {
			navigate('/confirmation');
		}
	}, [submitCompleted, submitResult, submitError, navigate]);

	const handleSubmit = () => {
		if (cardBrandError) return;

		if (validateForm()) {
			const {
				firstName,
				lastName,
				address1,
				address2,
				city,
				state,
				zipCode,
				email,
				phone,
				cardNumber,
				expiryDate,
				cvc,
				billingZip,
				typeOfCard
			} = formData;

			const unformattedCardNumber = cardNumber.replace(/[ ]/g, '');
			const unformattedExpireDate = expiryDate.replace(/[ /]/g, '');

			dispatch(
				setCardDisplayInfo({
					lastFour: unformattedCardNumber.slice(-4),
					cardType: typeOfCard?.toUpperCase() || ''
				})
			);

			const payload = {
				cardInfo: encryptCheckout({
					cardNumber: unformattedCardNumber,
					expiryDate: unformattedExpireDate,
					cvv: cvc
				}),
				nameOnCard: `${firstName} ${lastName}`,
				typeOfCard: typeOfCard?.charAt(0).toUpperCase() || '',
				firstName,
				lastName,
				address1,
				address2,
				city,
				state,
				zip: zipCode.replace(/\D/g, ''),
				billingZip: billingZip.replace(/\D/g, ''),
				emailAddress: email,
				country: 'US',
				phoneNumber: phone.replace(/\D/g, ''),
				browserSignature: signatureObj.signatureKey,
				campaignId: result.marketing_code,
				trackingNumber: result.trackingNumber,
				clubCategory: membershipListData.clubCategory
			};

			dispatch(postNewMembershipSignup(payload));

			reset();
			paymentInfoRef.current?.clearFields();
		}
	};

	return (
		<React.Fragment>
			<Backdrop
				sx={{
					color: 'white',
					zIndex: (theme) => theme.zIndex.drawer + 1,
					flexDirection: 'column',
					gap: 2
				}}
				open={submitDataIsLoading}
			>
				<CircularProgress color='inherit' />
				<Typography variant='body1'>{t('processingOrder')}</Typography>
			</Backdrop>

			<Stack
				direction={{ xs: 'column', md: 'row' }}
				spacing={4}
				sx={{ width: '100%' }}
				alignItems='flex-start'
			>
				<Box
					sx={{
						flex: { xs: 1, md: 2 },
						px: 3
					}}
				>
					<Typography variant='h5' sx={{ fontWeight: 600, mb: 2 }}>
						{t('myCart')}
					</Typography>

					<Stack spacing={3}>
						<BillingInfo
							formData={formData}
							errors={errors}
							handleInputChange={handleInputChange}
							handleInputBlur={handleInputBlur}
						/>

						<AgreeToChargeCard
							membershipListData={membershipListData}
							formData={formData}
							handleInputChange={handleInputChange}
							errors={errors}
						/>

						<MoneyBackGuarantee membershipListData={membershipListData} />
					</Stack>
				</Box>

				<Stack
					sx={{
						flex: 1.5,
						padding: 2,
						border: '1px solid',
						borderColor: 'divider',
						borderRadius: 6,
						alignSelf: 'flex-start',
						justifyContent: 'center',
						width: { xs: '100%', md: 'auto' }
					}}
					spacing={2}
				>
					<OrderSummary membershipListData={membershipListData} />
					{cardBrandError && <Alert severity='error'>{cardBrandError}</Alert>}
					<PaymentInfo
						ref={paymentInfoRef}
						formData={formData}
						errors={errors}
						handleInputChange={handleInputChange}
						handleInputBlur={handleInputBlur}
					/>
					<LoadingButton
						variant='contained'
						type='submit'
						onClick={handleSubmit}
						loading={submitDataIsLoading}
						disabled={!isFormComplete}
						color='success'
						sx={{
							color: 'primary',
							py: 1.5,
							fontSize: '1rem',
							fontWeight: 600,
							textTransform: 'none',
							borderRadius: '50px',
							'&:hover': {
								backgroundColor: 'success'
							},
							'&.Mui-disabled': {
								backgroundColor: 'grey.300',
								color: 'grey.500'
							}
						}}
					>
						{t('placeOrder')}
					</LoadingButton>
					<Guarantees membershipListData={membershipListData} />
				</Stack>
			</Stack>
		</React.Fragment>
	);
};

CheckoutForm.propTypes = {
	membershipListData: PropTypes.object.isRequired
};

export default CheckoutForm;
